// src/MarkdownRenderer.tsx

import React, { useMemo } from 'react';
import ReactMarkdown from 'react-markdown';
// @ts-ignore
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
// @ts-ignore
import { oneDark } from 'react-syntax-highlighter/dist/esm/styles/prism';
import remarkGfm from 'remark-gfm';
import remarkBreaks from 'remark-breaks';
import './MarkdownRenderer.css';

interface Props {
  content: string;
  streaming?: boolean;
  onCodePanelOpen?: (code: string, language: string) => void;
}

interface CodeProps {
  inline?: boolean;
  className?: string;
  children?: React.ReactNode;
  [key: string]: any;
}

// 언어 → 추정 확장자
const LANG_EXT: Record<string, string> = {
  javascript: 'js', typescript: 'ts', jsx: 'jsx', tsx: 'tsx',
  java: 'java', python: 'py', json: 'json', xml: 'xml',
  html: 'html', css: 'css', markdown: 'md', sql: 'sql',
  bash: 'sh', shell: 'sh', yaml: 'yaml', yml: 'yaml',
  kotlin: 'kt', swift: 'swift', go: 'go', rust: 'rs',
  cpp: 'cpp', c: 'c', csharp: 'cs', ruby: 'rb', php: 'php',
};

function makeCodeBlock(
  onCodePanelOpen?: (code: string, language: string) => void
) {
  return function CodeBlock({ inline, className, children, ...props }: CodeProps) {
    const match = /language-(\w+)/.exec(className || '');
    const language = match ? match[1] : 'text';
    const codeString = typeof children === 'string'
      ? children.replace(/\n$/, '')
      : String(children ?? '').replace(/\n$/, '');

    const [copied, setCopied] = React.useState(false);

    const handleCopy = (e: React.MouseEvent) => {
      e.stopPropagation();
      navigator.clipboard.writeText(codeString);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    };

    const handleOpenPanel = (e: React.MouseEvent) => {
      e.stopPropagation();
      onCodePanelOpen?.(codeString, language);
    };

    if (!inline && match) {
      return (
        <div className="conve-code-block-wrap">
          <div className="conve-code-header">
            <span className="conve-code-lang">{language}</span>
            <div className="conve-code-actions">
              <button className="conve-code-btn" onClick={handleCopy}>
                {copied ? '✓' : '복사'}
              </button>
              {onCodePanelOpen && (
                <button className="conve-code-btn primary" onClick={handleOpenPanel}>
                  패널에서 보기 ↗
                </button>
              )}
            </div>
          </div>
          <SyntaxHighlighter
            style={oneDark}
            language={language}
            PreTag="div"
            customStyle={{ margin: 0, borderRadius: '0 0 6px 6px' }}
            {...props}
          >
            {codeString}
          </SyntaxHighlighter>
        </div>
      );
    }

    return (
      <code className={className} {...props}>
        {children}
      </code>
    );
  };
}

function normalizeStreamingMarkdown(text: string): string {
  const lines = text.split('\n');
  const result: string[] = [];

  for (let i = 0; i < lines.length; i++) {
    const line = lines[i];
    const trimmed = line.trimStart();
    const prevLine = i > 0 ? result[result.length - 1] : '';

    const isBlockStart =
      /^#{1,6}\s/.test(trimmed) ||
      /^#{1,6}[^\s#]/.test(trimmed) ||
      /^[-*+]\s/.test(trimmed) ||
      /^\d+\.\s/.test(trimmed) ||
      /^```/.test(trimmed) ||
      /^>\s/.test(trimmed) ||
      /^\|/.test(trimmed) ||
      /^---/.test(trimmed);

    if (isBlockStart && prevLine !== undefined && prevLine.trim() !== '') {
      result.push('');
    }

    result.push(line);
  }

  return result.join('\n');
}

// 언어 지정된 블록 우선, 없으면 언어 없는 블록
export function extractLongestCodeBlock(content: string): { code: string; language: string } | null {
  const regex = /```(\w*)\n([\s\S]*?)```/g;
  let match;
  let bestWithLang: { code: string; language: string } | null = null;
  let bestNoLang: { code: string; language: string } | null = null;

  while ((match = regex.exec(content)) !== null) {
    const language = match[1] || '';
    const code = match[2];

    if (language && language !== 'text') {
      // 언어 지정된 블록 중 가장 긴 것
      if (!bestWithLang || code.length > bestWithLang.code.length) {
        bestWithLang = { code, language };
      }
    } else {
      // 언어 없는 블록 중 가장 긴 것
      if (!bestNoLang || code.length > bestNoLang.code.length) {
        bestNoLang = { code, language: 'text' };
      }
    }
  }

  // 언어 지정된 블록 우선, 없으면 언어 없는 블록
  return bestWithLang ?? bestNoLang;
}

const MarkdownRenderer: React.FC<Props> = ({ content, streaming = false, onCodePanelOpen }) => {
  const markdownContent = useMemo(() => {
    if (!streaming) return content;

    const lines = content.split('\n');
    let fenceCount = 0;
    for (const line of lines) {
      if (line.trimStart().startsWith('```')) fenceCount++;
    }
    const insideCodeBlock = fenceCount % 2 === 1;

    if (insideCodeBlock) return content + '\n```';
    return normalizeStreamingMarkdown(content);
  }, [content, streaming]);

  const plugins = streaming ? [remarkGfm, remarkBreaks] : [remarkGfm];

  const components = useMemo(() => ({
    code: makeCodeBlock(onCodePanelOpen),
  }), [onCodePanelOpen]);

  return (
    <div className="markdown-body">
      <ReactMarkdown remarkPlugins={plugins} components={components}>
        {markdownContent}
      </ReactMarkdown>
    </div>
  );
};

export { LANG_EXT };
export default MarkdownRenderer;
